import { Box, Progress, Table, TableContainer, Tbody, Td, Tr } from "@chakra-ui/react";
import { cloneDeep, debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { FunctionComponent, ReactElement } from "react";
import "./DataTable.css";

// Components
import DataTableHeader from "./components/DataTableHeader";
import DataTableRow from "./components/DataTableRow";
import DataTablePagination from "./components/DataTablePagination";
import DataTableHeaderActions from "./components/DataTableHeaderActions";

// Types
// import { DataTableColumn, PaginatedApiResponse, PaginationResponse } from "../DataTable/types";
import { KVP, SortDirection, DataTableColumn, PaginatedApiResponse, PaginationResponse } from "../../utils/types";

// Hooks
import useClaimsSearchParams, { IParams, StaticKeys } from "./hooks/useSearchParams";

interface DataTableProps {
	columns: Array<DataTableColumn>;
	data: PaginatedApiResponse;
	/**
	 * An optional title for this table
	 */
	title?: string;

	/**
	 * Did we land on a page with existing parameters?
	 */
	existingParams?: URLSearchParams;

	/**
	 * Does the consumer have some default parameters to use?
	 */
	defaultParams?: Partial<IParams>;

	/**
	 * Additional filters managed outside the table's visible columns.
	 */
	additionalSearchModel?: KVP;

	/**
	 * Should the table show that it is loading?
	 */
	isLoading?: boolean;

	/**
	 * Custom Header elements to be added to the Table's Custom Header actions.
	 * All functionality must be leveraged by the provider
	 */
	customHeaderActions?: ReactElement;

	/**
	 * Custom actions to show on the right side of the header, next to the filter toggle button
	 * Receives selected rows so export/actions can use them
	 */
	customRightActions?: ReactElement | ((selectedRows: Array<KVP>) => ReactElement);

	/**
	 * Defines whether the search params should update the URL or not
	 * Defaults to TRUE
	 */
	shouldUpdateUrl?: boolean;

	/**
	 * Defines whether the table container should be shown or not
	 * Defaults to TRUE
	 */
	shouldShowTableContainer?: boolean;

	/**
	 * Defines whether the table container should be shown or not
	 * Defaults to TRUE
	 */
	shouldAllowExport?: boolean;

	/**
	 * Should the Table append the showDisabled param to the query string?
	 * Defaults to FALSE
	 */
	shouldShowDisabled?: boolean;

	/**
	 * Should the table display the column for selection?
	 * Defaults to TRUE
	 */
	shouldShowSelection?: boolean;

	/**
	 * Should the table display the toggle for filters?
	 * Defaults to TRUE
	 */
	shouldShowFilterToggle?: boolean;

	/**
	 * Should the table display buttons for pagination?
	 * Defaults to TRUE
	 */
	shouldShowPagination?: boolean;

	/**
	 * Should the Table display the Advanced Search Toggle?
	 * Defaults to TRUE
	 * TODO: Remove this when Advanced Search implementation is finished
	 */
	// advancedSearchEnabled?: boolean;

	/**
	 * Consumer updates are triggered through this function. Given the updated cacheKey,
	 * the consumer can trigger refetching API data automatically through this property
	 * @param cacheKey updated cacheKey from useSearchParams
	 */
	refetchTrigger: (cacheKey: string) => void;

	/**
	 * Should the consumer provide the ability to export data to CSV format, this callback
	 * will be used to provide data to the header function if no rows are already selected
	 * @param key The current CacheKey, appended to the request as a Query String
	 * @param meta API returned Meta information relevant to the request
	 */
	// exportHandler: (key: string, meta: PaginationResponse) => Promise<PaginatedApiResponse | null>;

	/**
	 * Handles displaying custom Table Header elements on the right-side of the Table header
	 * Particularly used for selected row-specific actions, such as disables, sending emails, etc
	 * @param selectedRows Currently selected rows of the table
	 * @param clearSelectedRows Delegate to handle clearing the selected rows
	 */
	customSelectedActions?: (selectedRows: Array<KVP>, clearSelectedRows: () => void) => ReactElement;

	/**
	 * Handles providing the contents for the Actions Menu column if the feature is enabled
	 * @param currentRow
	 * @returns
	 */
	customActionsMenuContents?: (currentRow: KVP) => ReactElement | undefined;

	/**
	 * Optional Validation function. If the consumer's model requires validation on certain
	 * field, the validation is automatically invoked whenever a debounced update to the
	 * model is triggered
	 * @param model typeof: KVP, the mapped search parameter object with which to validate
	 */
	modelValidation?: (model: KVP) => boolean;
}

/**
 * In order to make dynamic use of search parameters, map the table columns to the
 * parameter model as a key-value pair.
 * @param columns typeof: Array<DataTableColumn>
 * @returns KVP Search Parameter Model
 */
const mapColumnsToSearchParams = (
	columns: Array<DataTableColumn>,
	existingParams?: URLSearchParams,
	defaultParams?: Partial<IParams>
): KVP => {
	// Map columns to search model
	const mapObject: KVP = {};
	const existingParamsObject: KVP = {};

	if (defaultParams) {
		// Basically do the same as below
		const searchFields = defaultParams[StaticKeys.SearchField]?.split(",") ?? [];
		const searchValues = defaultParams[StaticKeys.SearchString]?.split(",") ?? [];

		searchFields?.forEach((field: string, index: number) => {
			existingParamsObject[field] = searchValues[index];
		});
	}

	if (existingParams) {
		// Map the existing params to a model object for later
		const searchFields = existingParams.get(StaticKeys.SearchField)?.split(",") ?? [];
		const searchValues = existingParams.get(StaticKeys.SearchString)?.split(",") ?? [];

		// The value should exist at the same index as the key
		searchFields?.forEach((field: string, index: number) => {
			existingParamsObject[field] = searchValues[index];
		});
	}

	columns.forEach((column: DataTableColumn) => {
		// If previous params exist, use those values
		if (defaultParams) mapObject[column.id] = existingParamsObject[column.id];
		if (existingParams) mapObject[column.id] = existingParamsObject[column.id];
		// If no previous params, use an empty string
		else mapObject[column.id] = "";
	});

	return mapObject;
};

/**
 * Helper function to determine whether search params already exist on page load
 * Useful in the scenario in which a user navigates to a URL with params already present
 * Used to toggle the search fields
 * @param existingParams typeof URLSearchParams
 * @returns boolean
 */
// const doSearchParamsExist = (existingParams?: URLSearchParams): boolean => !!existingParams && existingParams.toString().length > 0;

/**
 * Simple Data Table implementation to display data in a clear and concise manner to the user.
 * Handles certain stock actions such as: Row Selection, Column Searching/Sorting, Table Exports,
 * and Pagination
 * @param props typeof: DataTableProps
 */
const DataTable: FunctionComponent<DataTableProps> = (props: DataTableProps): ReactElement => {
	const {
		columns,
		data,
		title,
		customHeaderActions,
		customRightActions,
		existingParams,
		defaultParams,
		additionalSearchModel,
		isLoading,
		shouldUpdateUrl = true,
		shouldShowTableContainer = true,
		shouldAllowExport = true,
		shouldShowDisabled = false,
		shouldShowSelection = true,
		shouldShowFilterToggle = true,
		shouldShowPagination = true,
		// advancedSearchEnabled = true,
		refetchTrigger,
		// exportHandler,
		customSelectedActions,
		customActionsMenuContents,
		modelValidation
	} = props;
	const [searchModel, setSearchModel] = useState<KVP>(() => ({
		...mapColumnsToSearchParams(columns, existingParams, defaultParams),
		...additionalSearchModel
	}));
	const [selectedRows, setSelectedRows] = useState<Array<string>>([]);
	const [searchEnabled, setSearchEnabled] = useState<boolean>(true);

	const firstPass = useRef(true);

	// Debounce this so we're not firing updates on every keystroke
	// Change debounce timer as desired
	const debounceSetParamModelHandler = debounce((model: KVP) => {
		setParamModel(model);
		// Revert to page 1 when search is triggered
		if (firstPass.current !== true) {
			setCurrentPage(1);
		}
		if (firstPass.current) firstPass.current = false;
	}, 1200);
	// React Function Components recreate internal functions on every re-render, therefore we need
	// to save the ref to the debounce handler in a useCallback hook
	const debounceSetParamModel = useCallback((model: KVP) => debounceSetParamModelHandler(model), []);

	// Invoke the useSearchParam hook with certain default values
	const {
		currentSortField,
		currentSortDirection,
		currentPage,
		currentLimit,

		setCurrentPage,
		//@TODO: Finish implementing this
		// setCurrentLimit,
		setCurrentSortField,
		setCurrentSortDirection,
		setParamModel,
		getCacheKey
	} = useClaimsSearchParams({
		params: {
			limit: defaultParams?.limit ?? 20,
			model: defaultParams?.model ?? searchModel,
			page: defaultParams?.page ?? 1,
			sortDirection: defaultParams?.sortDirection ?? SortDirection.Asc,
			sortField: defaultParams?.sortField ?? "id",
			searchField: defaultParams?.searchField ?? "",
			searchString: defaultParams?.searchString ?? ""
		},
		existingParams: existingParams,
		shouldUpdateUrl: shouldUpdateUrl,
		shouldShowDisabled: shouldShowDisabled,
		validate: modelValidation,
		callback: (cacheKey: string) => {
			refetchTrigger(cacheKey);
		}
	});

	/**
	 * Helper function to return Data Table Columns
	 * @returns cloned Array<DataTableColumn>
	 */
	const getTableColumns = (): Array<DataTableColumn> => cloneDeep(columns);

	/**
	 * Helper function to return the currently selected rows
	 * @returns typeof: Array<KVP>
	 */
	const getSelectedTableRows = (): Array<KVP> =>
		data.data.filter((row: KVP) => selectedRows.indexOf(row["id"].toString()) > -1);

	/**
	 * Helper function to determine whether the given row is currently selected
	 * @param row typeof: KVP, the row with which to determine whether it is selected
	 * @returns typeof: boolean
	 */
	const isRowSelected = (row: KVP): boolean => selectedRows.indexOf(row["id"].toString()) > -1;

	/**
	 * Handles updating the list of currently selected rows
	 * @param rowId ID of the given row
	 * @param isChecked Whether the row is checked or not
	 */
	const handleSelectUpdate = (rowId: string, isChecked: boolean): void => {
		// Clone this so we don't update "immutable" properties
		const tmpSelectedRows = cloneDeep(selectedRows);
		if (isChecked) {
			tmpSelectedRows.push(rowId);
		} else {
			const removeIndex: number = tmpSelectedRows.indexOf(rowId);
			tmpSelectedRows.splice(removeIndex, 1);
		}
		setSelectedRows(tmpSelectedRows);
	};

	/**
	 * Handles bulk selection/deselection of rows
	 * @param isChecked Whether all rows on the current page are checked or not
	 */
	const handleBulkSelectUpdate = (isChecked: boolean): void => {
		// If unchecked, simply set an empty array
		if (!isChecked) setSelectedRows([]);
		else {
			// Construct a list of IDs
			const rowIds: Array<string> = data.data.map((row: KVP) => row["id"].toString());
			// Update the rows
			setSelectedRows(rowIds);
		}
	};

	/**
	 * Simple helper function to allow external components to clear selected rows
	 */
	const handleClearSelectedRows = () => setSelectedRows([]);

	/**
	 * Handles toggling whether search fields are enabled or not
	 */
	const handleSearchToggle = (): void => setSearchEnabled(!searchEnabled);

	/**
	 * Whenever the search model is updated, debounce the update so we
	 * don't make dozens of updates per second
	 */
	useEffect(() => {
		debounceSetParamModel(searchModel);
	}, [searchModel]);

	useEffect(() => {
		if (additionalSearchModel) {
			setSearchModel((currentModel) => ({ ...currentModel, ...additionalSearchModel }));
		}
	}, [additionalSearchModel]);

	return (
		<Box>
			<TableContainer
				mx="auto"
				background="white"
				padding={shouldShowTableContainer ? 5 : 0}
				boxShadow={shouldShowTableContainer ? "1px 2px 9px 1px gainsboro" : "none"}>
				<DataTableHeaderActions
					title={title}
					getCacheKey={getCacheKey}
					getTableColumns={getTableColumns}
					meta={data.meta}
					searchEnabled={searchEnabled}
					exportEnabled={shouldAllowExport}
					toggleSearch={handleSearchToggle}
					showFilterToggle={shouldShowFilterToggle}
					customHeaderActions={customHeaderActions}
					customRightActions={
						typeof customRightActions === "function" ? customRightActions(getSelectedTableRows()) : customRightActions
					}
					getSelectedTableRows={getSelectedTableRows}
					clearSelectedRows={handleClearSelectedRows}
					customSelectedActions={customSelectedActions}
					// exportHandler={exportHandler}
					// advancedSearchEnabled={advancedSearchEnabled}
				/>
				<Table size="sm" variant="striped" mr={0} background="white">
					<DataTableHeader
						columns={columns}
						searchModel={searchModel}
						setSearchModel={setSearchModel}
						sortDirection={currentSortDirection}
						sortDirectionHandler={setCurrentSortDirection}
						sortField={currentSortField}
						sortFieldHandler={setCurrentSortField}
						searchEnabled={searchEnabled}
						handleBulkSelect={handleBulkSelectUpdate}
						selectionEnabled={shouldShowSelection}
						isLoading={isLoading}
					/>
					<Tbody className={isLoading ? "loading" : undefined}>
						{data.data.map((row: KVP) => (
							<DataTableRow
								key={`datatable-${row.id}`}
								isSelected={isRowSelected(row)}
								row={row}
								columns={columns}
								updateSelected={handleSelectUpdate}
								selectionEnabled={shouldShowSelection}
							/>
						))}
					</Tbody>
				</Table>
				{shouldShowPagination && (
					<DataTablePagination
						isDisabled={isLoading}
						currentPage={currentPage}
						limit={currentLimit}
						meta={data.meta}
						setCurrentPage={setCurrentPage}
					/>
				)}
			</TableContainer>
		</Box>
	);
};

export default DataTable;
